import useLocation from './use-location'

const DEFAULT_PAGE_SIZE = 24

interface Brand {
  key: string
}

interface EarnRateOption {
  key: string
  comparisonOperator?: string
}

interface PriceParams {
  minPrice?: number | null
  maxPrice?: number | null
  onlyItemsWithinBalance?: boolean | null
}

interface SearchQueryParams {
  q?: string | null
  qType?: string | null
}

// Make sure to update breadcrumbs related UI and functionality in case of changing
// some filters (eg: subCategory)
export const useFilter = () => {
  const { query, setQueryParams, clearQueryParams } = useLocation()

  // filters:
  const sort = query?.sort || ''

  const currentPage = query?.currentPage ? parseInt(query?.currentPage) : 1

  const itemsPerPage =
    (query?.itemsPerPage && parseInt(query.itemsPerPage)) || DEFAULT_PAGE_SIZE

  const selectedBrands: string[] =
    query?.brands?.split(',').map((i: string) => i.trim()) || []

  const selectedEarnRates: Array<[string, string]> = query.earnRates
    ? JSON.parse(decodeURIComponent(query.earnRates))
    : []

  const selectedCategories: string[] =
    query?.categories?.split(',').map((i: string) => i.trim()) || []

  const minPrice: number = query?.minPrice || 0
  const maxPrice: number = query?.maxPrice || 0

  const searchQuery = query?.q || ''
  const searchType = query?.searchType || ''

  const subCategory = query?.subCategory || ''

  const onlyItemsWithinBalance = query?.onlyItemsWithinBalance === 'true'

  // filters setters:

  const clearFilters = () => {
    // clear all filters except searchQuery, sort and subCategory
    if (sort || subCategory || searchQuery || searchType) {
      setQueryParams(
        {
          sort,
          subCategory,
          q: searchQuery,
          searchType,
        },
        true,
      )
      return
    }
    clearQueryParams()
  }

  const clearAndOverrideFilter = (exceptions: Record<string, any> = {}) => {
    const preservedParams = Object.entries(exceptions).reduce<
      Record<string, any>
    >((acc, [key, value]) => {
      // Only include keys with non-empty and non-null values
      if (!!value) {
        acc[key] = value
      }
      return acc
    }, {})

    setQueryParams(preservedParams, true)
  }

  const setSort = (sort: string) => setQueryParams({ sort })

  const setCurrentPage = (currentPage: number) =>
    setQueryParams({ currentPage })

  const setItemsPerPage = (itemsPerPage: number) =>
    setQueryParams({ itemsPerPage })

  const setPageInfo = (
    currentPage: number,
    itemsPerPage: number,
    sort?: string,
  ) => {
    setQueryParams({ currentPage, itemsPerPage, sort })
  }

  const setMinMaxPrice = ({ minPrice, maxPrice }: PriceParams) => {
    const queryParams: PriceParams = {}

    queryParams.minPrice = minPrice && minPrice > 0 ? minPrice : null
    queryParams.maxPrice = maxPrice && maxPrice > 0 ? maxPrice : null

    // Disables the onlyItemsWithinBalance filter when either minPrice or maxPrice is set.
    if (minPrice || maxPrice) queryParams.onlyItemsWithinBalance = false

    setQueryParams({ ...queryParams, currentPage: 1 })
  }

  const setSearchQuery = ({ q, qType }: SearchQueryParams) => {
    setQueryParams({ q, searchType: qType }, false)
  }

  const setSubCategory = (subCategory: string) =>
    setQueryParams({ subCategory, currentPage: 1 }, false)

  const addToSelectedBrand = (brand: Brand) => {
    const updatedBrands = selectedBrands?.includes(brand.key)
      ? selectedBrands?.filter(
          (selectedBrand: string) => selectedBrand !== brand.key,
        )
      : [...selectedBrands, brand.key]

    setQueryParams({ brands: updatedBrands, currentPage: 1 })
  }

  const addToSelectedEarnRates = (earnRate: EarnRateOption) => {
    // Create the earnRateWithOperator as an array
    const earnRateWithOperator = [earnRate.comparisonOperator, earnRate.key]

    // Determine if the earnRate key is already included in the selectedEarnRates
    const isAlreadySelected = selectedEarnRates.some(
      (er) => er[1] === earnRate.key, // Assuming the second element is the key
    )

    // Update the selectedEarnRates array
    const updatedEarnRates = isAlreadySelected
      ? selectedEarnRates.filter((er) => er[1] !== earnRate.key)
      : [...selectedEarnRates, earnRateWithOperator]

    // Update query parameters using setQueryParams from useLocation()
    setQueryParams({
      earnRates: JSON.stringify(updatedEarnRates),
      currentPage: 1,
    })
  }

  const selectedEarnRatesExpressions = () => {
    return selectedEarnRates.map((rate) => {
      // Each 'rate' is assumed to be an array with the form [comparisonOperator, key]
      const comparisonOperator = rate[0] // Extract the comparison operator
      const key = rate[1] // Extract the key
      return [comparisonOperator, key]
    })
  }

  const isEarnRateSelected = (key: string) => {
    return selectedEarnRates?.some((rate: any[]) => rate[1] === key)
  }

  // Fix type
  const addToSelectedCategory = (category: any) => {
    const updatedCategories = selectedCategories?.includes(category.key)
      ? selectedCategories?.filter(
          (selectedCategory: string) => selectedCategory !== category.key,
        )
      : [...selectedCategories, category.key]

    setQueryParams({ categories: updatedCategories, currentPage: 1 })
  }

  const setOnlyItemsWithinBalance = (onlyItemsWithinBalance: boolean) => {
    // Sets onlyItemsWithinBalance and clears both minPrice and maxPrice to ensure filter consistency.
    setQueryParams({
      onlyItemsWithinBalance,
      minPrice: null,
      maxPrice: null,
      currentPage: 1,
    })
  }

  return {
    sort,
    setSort,
    currentPage,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
    setPageInfo,
    minPrice,
    maxPrice,
    setMinMaxPrice,
    subCategory,
    setSubCategory,
    searchQuery,
    setSearchQuery,
    searchType,
    selectedEarnRates,
    addToSelectedEarnRates,
    selectedBrands,
    addToSelectedBrand,
    selectedCategories,
    addToSelectedCategory,
    clearFilters,
    clearAndOverrideFilter,
    onlyItemsWithinBalance,
    setOnlyItemsWithinBalance,
    selectedEarnRatesExpressions,
    isEarnRateSelected,
  }
}
